import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Clock, Mail, CheckCircle, AlertCircle } from 'lucide-react';

interface UpgradeStatusBadgeProps {
  hasUpgrade: boolean;
  upgradeStatus?: string;
  gracePeriodEnd?: string;
  className?: string;
  metadata?: Record<string, any>;
}

export const UpgradeStatusBadge: React.FC<UpgradeStatusBadgeProps> = ({
  hasUpgrade,
  upgradeStatus,
  gracePeriodEnd,
  className = '',
  metadata
}) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          variant: 'secondary' as const,
          icon: <Clock className="h-3 w-3" />,
          label: 'Upgrade Pending',
          tooltip: 'Subscription upgrade is pending - email will be sent to customer',
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'email_sent':
        return {
          variant: 'default' as const,
          icon: <Mail className="h-3 w-3" />,
          label: 'Email Sent',
          tooltip: gracePeriodEnd 
            ? `Customer notified about upgrade - grace period ends ${new Date(gracePeriodEnd).toLocaleDateString()}`
            : 'Customer has been notified about the upcoming subscription upgrade',
          color: 'bg-blue-100 text-blue-800 border-blue-200'
        };
      case 'payment_method_updated':
        return {
          variant: 'default' as const,
          icon: <CheckCircle className="h-3 w-3" />,
          label: 'ACH Updated',
          tooltip: 'Customer switched to ACH payment - no price increase applied',
          color: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'scheduled_for_upgrade':
        return {
          variant: 'destructive' as const,
          icon: <AlertCircle className="h-3 w-3" />,
          label: 'Scheduled',
          tooltip: 'Upgrade is scheduled to be applied at next billing cycle',
          color: 'bg-orange-100 text-orange-800 border-orange-200'
        };
      case 'upgraded':
        return {
          variant: 'default' as const,
          icon: <CheckCircle className="h-3 w-3" />,
          label: 'Upgraded',
          tooltip: 'Subscription has been successfully upgraded',
          color: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'failed':
        return {
          variant: 'destructive' as const,
          icon: <AlertCircle className="h-3 w-3" />,
          label: 'Failed',
          tooltip: 'Subscription upgrade failed',
          color: 'bg-red-100 text-red-800 border-red-200'
        };
      case 'cancelled':
        return {
          variant: 'secondary' as const,
          icon: <Clock className="h-3 w-3" />,
          label: 'Cancelled',
          tooltip: 'Subscription upgrade was cancelled',
          color: 'bg-gray-100 text-gray-800 border-gray-200'
        };
      case 'expired':
        return {
          variant: 'secondary' as const,
          icon: <Clock className="h-3 w-3" />,
          label: 'Expired',
          tooltip: 'Grace period has expired without action',
          color: 'bg-gray-100 text-gray-800 border-gray-200'
        };
      default:
        return {
          variant: 'outline' as const,
          icon: <Clock className="h-3 w-3" />,
          label: 'In Progress',
          tooltip: 'Subscription upgrade is in progress',
          color: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  };

  // Check if we have metadata that indicates a payment method update
  const hasPaymentMethodUpdate = metadata && metadata.updateMethodPaymentByKeepMore === 'true';
  
  // Check if we have metadata that indicates a price upgrade
  const hasPriceUpgrade = metadata && metadata.updatedByKeepMore === 'true';
  
  // If no upgrade status but we have metadata flags, show the appropriate badge
  if (!hasUpgrade && (hasPaymentMethodUpdate || hasPriceUpgrade)) {
    if (hasPaymentMethodUpdate) {
      return renderBadge('payment_method_updated');
    }
    if (hasPriceUpgrade) {
      return renderBadge('upgraded');
    }
  }
  
  if (!hasUpgrade) {
    return null;
  }

  return renderBadge(upgradeStatus || 'pending');
  
  function renderBadge(status: string) {
    const config = getStatusConfig(status);
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant={config.variant}
              className={`${config.color} flex items-center gap-1 text-xs font-medium border-full ${className}`}
            >
              {config.icon}
              {config.label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-sm">{config.tooltip}</p>
            {gracePeriodEnd && upgradeStatus === 'email_sent' && (
              <p className="text-xs text-muted-foreground mt-1">
                Grace period: {new Date(gracePeriodEnd).toLocaleDateString()}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
};
